import React, { createContext, useContext, useEffect, useState } from 'react';
import ApiClient from '../Services/ApiClient';
import AuthContext from './authContext';

type ResourcesMap = Record<string, string>;

interface ResourcesContextValue {
    resources: ResourcesMap;
    get: (key: string, fallback?: string) => string;
    culture: string | null;
    siteId: string | null;
    setCulture: (c: string | null) => void;
    reload: () => Promise<void>;
}

const ResourcesContext = createContext<ResourcesContextValue | undefined>(undefined);

// Helper to build query strings for resource endpoints. Kept at module scope so hooks can reuse it.
// Use 'siteId' as the query parameter name because server endpoints expect 'siteId'.
const buildQuery = (opts?: { keys?: string[]; culture?: string | null; siteId?: string | null }) => {
    const parts: string[] = [];
    if (opts?.keys && opts.keys.length > 0) parts.push(`keys=${encodeURIComponent(opts.keys.join(','))}`);
    if (opts?.culture) parts.push(`culture=${encodeURIComponent(opts.culture)}`);
    if (opts?.siteId) parts.push(`siteId=${encodeURIComponent(opts.siteId)}`);
    return parts.length > 0 ? `?${parts.join('&')}` : '';
};

export const ResourcesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [resources, setResources] = useState<ResourcesMap>({});
    // default culture to Hebrew ('he-IL') if not set
    const [culture, setCultureState] = useState<string | null>(() => {
        try { return localStorage.getItem('app_culture') || 'he-IL'; } catch { return 'he-IL'; }
    });

    const auth = React.useContext(AuthContext);
    // AuthContext.userId is number | null; convert to string or null
    // Note: use siteId if available in JWT; auth provider will expose siteId as siteId (string) if implemented
    const currentUserId = auth?.userId != null ? String(auth.userId) : null;

    // Resolved site id derived from multiple sources:
    // 1. If user is logged in and JWT contains siteId, use that (auth exposes siteId)
    // 2. Otherwise, resolve from hostname via public API and cache in localStorage for subsequent loads
    // default site id to 2004 for local/dev when not resolved
    const [resolvedSiteId, setResolvedSiteId] = useState<string | null>(() => {
        try { return localStorage.getItem('site_id') || '1'; } catch { return '1'; }
    });

    // effective site id used by queries: prefer JWT-provided siteId (after login), fall back to resolvedSiteId
    const jwtSiteId = (auth as any)?.siteId ? String((auth as any).siteId) : null;
    const currentSiteId = jwtSiteId ?? resolvedSiteId ?? '1';

    // Resolve site id from host when not available from JWT or cache
    useEffect(() => {
        if (jwtSiteId) {
            // store jwt site id in cache for consistency
            try { localStorage.setItem('site_id', jwtSiteId); } catch {}
            setResolvedSiteId(jwtSiteId);
            return;
        }
        if (resolvedSiteId) return; // already resolved

        const resolve = async () => {
            try {
                const host = typeof window !== 'undefined' ? window.location.hostname : null;
                if (!host) return;
                const resp: any = await ApiClient.get(`/api/sites/resolve?host=${encodeURIComponent(host)}`);
                const id = resp?.siteId ? String(resp.siteId) : null;
                if (id) {
                    try { localStorage.setItem('site_id', id); } catch {}
                    setResolvedSiteId(id);
                }
            } catch (e) {
                // don't block; leave resolvedSiteId null
                console.error('Failed to resolve site id from host', e);
            }
        };
        resolve();
    }, [jwtSiteId, resolvedSiteId]);

    // remove duplicate helper (module-level buildQuery is used)

    // Load global (fallback) resources into the global resources map
    const load = async () => {
        try {
            // Request component "Global" (server may treat Component==null differently).
        // We include siteId so server returns site-specific overrides plus defaults; server will enforce permissions.
            const q = buildQuery({ culture, siteId: currentSiteId });
            const data: any = await ApiClient.get(`/resources/component/Global${q}`);
            const map: ResourcesMap = {};
            // normalize response to an array of resource objects
            const list = (() => {
                if (!data) return [] as any[];
                if (Array.isArray(data)) return data as any[];
                // If single object with key/value, wrap it
                if (data.key) return [data];
                // If object keyed by resource keys, convert to array
                if (typeof data === 'object') return Object.keys(data).map(k => ({ key: k, value: (data as any)[k] }));
                return [] as any[];
            })();
            list.forEach((r: any) => { if (r && (r.key || r.Key)) map[r.key ?? r.Key] = r.value ?? r.Value; });
            setResources(map);
        } catch (e) {
            console.error('Failed to load resources', e);
        }
    };

    useEffect(() => { load(); }, [culture, currentSiteId]);

    const get = (key: string, fallback?: string) => {
        if (!key) return fallback ?? '';
        return resources[key] ?? fallback ?? '';
    };

    const setCulture = (c: string | null) => {
        try { if (c) localStorage.setItem('app_culture', c); else localStorage.removeItem('app_culture'); } catch {}
        setCultureState(c);
    };

    const reload = async () => { await load(); };

    return (
        <ResourcesContext.Provider value={{ resources, get, culture, siteId: currentSiteId, setCulture, reload }}>
            {children}
        </ResourcesContext.Provider>
    );
};

export const useResources = () => {
    const ctx = useContext(ResourcesContext);
    if (!ctx) throw new Error('useResources must be used within ResourcesProvider');
    return ctx;
};

// Helper hook to preload a specific list of keys (fetches only requested keys)
export const usePageResources = (keys: string[]) => {
    const [map, setMap] = React.useState<ResourcesMap>({});
    const { culture } = useResources();
    const { siteId } = useResources();
    const auth = React.useContext(AuthContext);
    const currentUserId = auth?.userId != null ? String(auth.userId) : null;
    const currentSiteId = siteId;

    React.useEffect(() => {
        if (!keys || keys.length === 0) return;
        const loadKeys = async () => {
            try {
                // compute siteId from auth at runtime to avoid referencing an out-of-scope variable
                const authCtx = React.useContext ? auth : null; // keep linter/tools happy (auth is captured above)
                const siteIdLocal = currentSiteId;
                const q = buildQuery({ keys, culture, siteId: siteIdLocal ?? '1' });
                const data: any = await ApiClient.get(`/resources/keys${q}`);
                const m: ResourcesMap = {};
                const list = Array.isArray(data) ? data : (data && data.key ? [data] : []);
                list.forEach((r: any) => { if (r && (r.key || r.Key)) m[r.key ?? r.Key] = r.value ?? r.Value; });
                setMap(m);
            } catch (e) {
                console.error('Failed to load page resources', e);
            }
        };
        loadKeys();
    }, [keys.join(','), culture, currentUserId, currentSiteId]);

    return map;
};

// New hook: load resources for a specific component (page/area). Prefer using this instead of usePageResources.
export const useComponentResources = (component: string, keys?: string[]) => {
    const [map, setMap] = React.useState<ResourcesMap>({});
    const { culture } = useResources();
    const { siteId } = useResources();
    const auth = React.useContext(AuthContext);
    const currentUserId = auth?.userId != null ? String(auth.userId) : null;
    const currentSiteId = siteId;

    React.useEffect(() => {
        if (!component) return;
        const load = async () => {
            try {
                // compute siteId inside effect to avoid runtime reference errors
                const siteIdLocal = currentSiteId;
                const q = buildQuery({ keys, culture, siteId: siteIdLocal ?? '1' });
                const url = `/resources/component/${encodeURIComponent(component)}${q}`;
                const data: any = await ApiClient.get(url);
                const m: ResourcesMap = {};
                const list = Array.isArray(data) ? data : (data && data.key ? [data] : []);
                list.forEach((r: any) => { if (r && (r.key || r.Key)) m[r.key ?? r.Key] = r.value ?? r.Value; });
                setMap(m);
            } catch (e) {
                console.error('Failed to load component resources', e);
            }
        };
        load();
    }, [component, (keys || []).join(','), culture, currentUserId, currentSiteId]);
        
    return map;
};
